import {
  IChartGridItemRequest,
  IGridItem,
  Loading,
  QUERIES,
  getPageGridItems,
  PageType,
  updatePageGridMatrice,
} from "@dataone/lib";
import { useQuery } from "@tanstack/react-query";
import { Col, Row } from "antd";
import { useEffect, useState } from "react";
import { styled } from "styled-components";

interface GridSelectionProps {
  pageId: string;
  setGridItem: React.Dispatch<React.SetStateAction<IChartGridItemRequest>>;
  selectedPage: { index: number; id: string; name: string };
  selectedDashboard: {
    index: number;
    id: string;
    name: string;
  };
  rows: number[];
  setRows: React.Dispatch<React.SetStateAction<number[]>>;
  columns: number[];
  setColumns: React.Dispatch<React.SetStateAction<number[]>>;
}

const GridSelection = ({
  pageId,
  setGridItem,
  selectedPage,
  selectedDashboard,
  rows,
  setRows,
  columns,
  setColumns,
}: GridSelectionProps) => {
  const wide = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const tight = [1, 2, 3, 4, 5, 6, 7, 8];
  const [type, setType] = useState<PageType>();

  // const [columns, setColumns] = useState<number[]>([]);
  // const [rows, setRows] = useState<number[]>([]);

  const [clickedCell, setClickedCell] = useState<{
    y: number;
    x: number;
  } | null>(null);
  const [originCell, setOriginCell] = useState<{
    y: number;
    x: number;
  } | null>(null);
  const [lastCell, setLastCell] = useState<{
    y: number;
    x: number;
  } | null>(null);
  const [selectedCells, setSelectedCells] = useState<
    | {
        y: number;
        x: number;
      }[]
    | null
  >(null);
  const [cellHovered, setCellHovered] = useState<{
    y: number;
    x: number;
  } | null>(null);

  const { data: page, isFetching } = useQuery({
    queryKey: [QUERIES.PAGE_GRID, pageId],
    queryFn: () => getPageGridItems(pageId),
    enabled: pageId !== "",
    onSuccess: ({ gridItems, type }) => {
      setColumns(type === PageType.PORTRAIT ? tight : wide);
      setRows(type === PageType.PORTRAIT ? wide : tight);
      setType(type);
      let grayedCells: { x: number; y: number }[] = [];
      (gridItems as Required<IGridItem>[]).forEach(({ x, y, w, h }) => {
        for (let i = x; i < x + w; i++) {
          for (let j = y; j < y + h; j++) {
            grayedCells.push({ x: i, y: j });
          }
        }
      });
      setGrayedOutCells(grayedCells);
    },
  });

  const [grayedOutCells, setGrayedOutCells] = useState<any[]>([]);

  const isCellBlocked = (y: number, x: number) => {
    return grayedOutCells.some((cell) => cell.y === y && cell.x === x);
  };

  const [clickCount, setClickCount] = useState(0);

  const handleGridClicked = (y: number, x: number) => {
    if (!isCellBlocked(y, x)) {
      setClickedCell({ y, x });
      setClickCount((prevCount) => prevCount + 1);
      if (clickCount === 0) {
        setOriginCell({ y, x });
        setSelectedCells([{ y, x }]);
      } else if (clickCount === 1) {
        setLastCell({ y, x });
        const topLeftCell = findTopLeftCell();
        const ChartHeight = calculateHeight();
        const ChartWeight = calculateWeight();
        const newGrid = {
          x: topLeftCell?.x || 0,
          y: topLeftCell?.y || 0,
          w: ChartWeight,
          h: ChartHeight,
        };
        page &&
          setGridItem({
            ...newGrid,
            grid: updatePageGridMatrice(page?.grid, [
              ...page?.gridItems,
              newGrid,
            ]),
          });
      } else if (clickCount === 2) {
        setClickCount(1);
        setOriginCell({ y, x });
        setSelectedCells([{ y, x }]);
        setLastCell(null);
      }
    }
  };

  const handleGridHover = (y: number, x: number) => {
    if (originCell === null || lastCell) {
      setCellHovered({ y, x });
    }
    if (clickedCell && lastCell === null) {
      let minY = Math.min(clickedCell.y, y);
      let maxY = Math.max(clickedCell.y, y);
      let minX = Math.min(clickedCell.x, x);
      let maxX = Math.max(clickedCell.x, x);

      let cellsToSelect: any = [];

      for (let i = minY; i <= maxY; i++) {
        for (let j = minX; j <= maxX; j++) {
          if (!isCellBlocked(i, j)) {
            cellsToSelect.push({ y: i, x: j });
          } else {
            if (clickedCell.y <= y && clickedCell.x <= x) {
              if (clickedCell.x === j) {
                for (let k = i; k <= maxY; k++) {
                  for (let l = minX; l <= maxX; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                maxY = i - 1;
                break;
              } else {
                for (let k = minY; k <= maxY; k++) {
                  for (let l = j; l <= maxX; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                maxX = j - 1;
                break;
              }
            } else if (clickedCell.y <= y && clickedCell.x > x) {
              if (clickedCell.x === j) {
                for (let k = i; k <= maxY; k++) {
                  for (let l = minX; l <= maxX; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                maxY = i - 1;
                break;
              } else {
                for (let k = minY; k <= maxY; k++) {
                  for (let l = minX; l <= j; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                minX = j + 1;
              }
            } else if (clickedCell.y > y) {
              if (clickedCell.y === i && x >= j && clickedCell.x < j) {
                for (let k = minY; k <= maxY; k++) {
                  for (let l = j; l <= maxX; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                maxX = j - 1;
                break;
              } else if (clickedCell.y === i && x <= j && clickedCell.x > j) {
                for (let k = minY; k <= maxY; k++) {
                  for (let l = minX; l <= j; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                minX = j + 1;
              } else {
                for (let k = minY; k <= i; k++) {
                  for (let l = minX; l <= maxX; l++) {
                    cellsToSelect = cellsToSelect.filter(
                      (cell: any) => cell.x !== l || cell.y !== k
                    );
                  }
                }
                minY = i + 1;
                break;
              }
            }
          }
        }
      }

      setSelectedCells(cellsToSelect);
    }
  };

  const handleGridLeave = () => {
    // setSelectedCells(null);
    if (originCell === null || lastCell) {
      setCellHovered(null);
    }
    if (originCell && lastCell === null) {
      setSelectedCells(null);
      setClickedCell(null);
      setClickCount(0);
      setOriginCell(null);
      setLastCell(null);
      setCellHovered(null);
    }
  };

  const findTopLeftCell = () => {
    if (!selectedCells || selectedCells.length === 0) {
      return null;
    }

    let topLeftCell = selectedCells[0];
    for (const cell of selectedCells) {
      if (
        cell.x < topLeftCell.x ||
        (cell.x === topLeftCell.x && cell.y < topLeftCell.y)
      ) {
        topLeftCell = cell;
      }
    }

    return topLeftCell;
  };

  const calculateHeight = () => {
    if (!selectedCells || selectedCells.length === 0) {
      return 0;
    }

    const minY = Math.min(...selectedCells.map((cell) => cell.y));
    const maxY = Math.max(...selectedCells.map((cell) => cell.y));

    return maxY - minY + 1;
  };

  const calculateWeight = () => {
    if (!selectedCells || selectedCells.length === 0) {
      return 0;
    }

    const minX = Math.min(...selectedCells.map((cell) => cell.x));
    const maxX = Math.max(...selectedCells.map((cell) => cell.x));

    return maxX - minX + 1;
  };
  useEffect(() => {
    setSelectedCells(null);
    setClickedCell(null);
    setClickCount(0);
    setOriginCell(null);
    setLastCell(null);
    setCellHovered(null);
  }, [pageId]);
  return (
    <Container>
      <StyledTitle>
        {selectedDashboard.id !== "" && selectedPage.id !== "" && (
          <>
            {selectedDashboard.name} / {selectedPage.name}
          </>
        )}
      </StyledTitle>
      <GridSelectionContainer>
        <StyledGridSelection
          $isLoading={isFetching}
          $type={type !== undefined ? type : PageType.PORTRAIT}
          onMouseLeave={handleGridLeave}
        >
          {isFetching ? (
            <StyledLoading>
              <Loading />
            </StyledLoading>
          ) : (rows.length < 1 && columns.length < 1) ||
            selectedPage.index === -1 ? (
            <EmptyPageConatiner>
              <img
                src="images/smart-search/pages-icon.svg"
                alt="pages"
                width={100}
              />
              <h3>Page</h3>
              <p>No Page Selected yet</p>
            </EmptyPageConatiner>
          ) : (
            rows.map((row, y) => {
              return (
                <Row key={y}>
                  {columns.map((column, x) => {
                    const isClicked =
                      clickedCell &&
                      selectedCells &&
                      selectedCells.some(
                        (cell) => cell.y === y && cell.x === x
                      );
                    const isBlocked = isCellBlocked(y, x);
                    const isHovered =
                      cellHovered &&
                      !isBlocked &&
                      cellHovered.x === x &&
                      cellHovered.y === y;
                    // const isHovered =
                    //   selectedCells &&
                    //   selectedCells.some((cell) => cell.y === y && cell.x === x);
                    return (
                      <Col
                        key={`${y}-${x}`}
                        className={`grid ${isClicked ? "grid-clicked" : ""} ${
                          isBlocked ? "grayed-out" : ""
                        } ${isHovered ? "hovered-cell" : ""}`}
                        onClick={() => handleGridClicked(y, x)}
                        onMouseEnter={() => handleGridHover(y, x)}
                        // onMouseLeave={handleGridLeave}
                      ></Col>
                    );
                  })}
                </Row>
              );
            })
          )}
        </StyledGridSelection>
      </GridSelectionContainer>
    </Container>
  );
};

export default GridSelection;

const GridSelectionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
const Container = styled.div`
  // min-width: 400px;
  width: 380px;
  min-height: 440px;
`;
const StyledTitle = styled.h3`
  font-size: 17px;
  margin-top: 13px;
  min-height: 30px;
`;
const StyledGridSelection = styled.div<{
  $isLoading: boolean;
  $type: PageType;
}>`
  width: max-content;
  margin: 107px 0px 20px 0px;
  ${(p) => p.$type === PageType.PORTRAIT && "margin: 16px 0px 0px 0px;"};
  padding: 5px;
  border-radius: 14.434px;

  ${(p) => !p.$isLoading && "border: 5px solid #4974f6;"}

  ${(p) => p.$isLoading && "width: 100%;"}
    background: #fff;

  .grid {
    width: 24px;
    height: 24px;
    border-radius: 9.623px;
    border: 0.5px solid #4974f6;
    background: #fff;
    // box-shadow: 0px 3.84906px 3.84906px 0px rgba(0, 0, 0, 0.25) inset;
    margin: 3px;
  }
  .grid-clicked {
    background: rgba(73, 116, 246, 1);
    cursor: pointer;
  }
  .grayed-out {
    background: #eff1fd;
    border-radius: 9.623px;
    border: 0px;
    // box-shadow: 0px 3.84906px 3.84906px 0px rgba(0, 0, 0, 0.25) inset;
  }
  .hovered-cell {
    background: rgba(73, 116, 246, 1);
    cursor: pointer;
  }
`;

const StyledLoading = styled.div`
  margin-left: 65px;
  width: 63%;
`;
const EmptyPageConatiner = styled.div`
  width: 384px;
  height: 367px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  h3 {
    color: #040726;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
  }
`;
